import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api/axios';
import LayoutWrapper from '../../components/Layout/LayoutWrapper';
import { LoadingSpinner, ErrorHandler } from '../../components/common/ErrorHandler';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { ArrowLeft, Edit, Printer, UserCircle, Phone, Mail, MapPin, Calendar } from 'lucide-react';
import { usePermission } from '../../components/Guards/PermissionGuard';
import { generateMembershipForm } from '../../components/prints/clubPrintTemplates';
import useToast from '../../hooks/useToast';
import { getImageUrl } from '../../utils/image';

// Helper function to calculate age from DOB
const calculateAge = (dob) => {
  if (!dob) return null;
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

const MemberView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const canEdit = usePermission('membership', 'edit');
  const { showSuccess, showError } = useToast();

  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMember();
  }, [id]);

  const fetchMember = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/members/${id}`);
      setMember(response.data);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (member) {
      try {
        const doc = generateMembershipForm(member);
        doc.save(`membership_form_${member.membership_no}.pdf`);
        showSuccess('PDF generated successfully');
      } catch (err) {
        showError('Failed to generate PDF');
        console.error('PDF generation error:', err);
      }
    }
  };

  if (loading) {
    return (
      <LayoutWrapper title="Member Details">
        <LoadingSpinner text="Loading member details..." />
      </LayoutWrapper>
    );
  }

  if (error) {
    return (
      <LayoutWrapper title="Member Details">
        <ErrorHandler error={error} onRetry={fetchMember} />
      </LayoutWrapper>
    );
  }

  if (!member) {
    return (
      <LayoutWrapper title="Member Details">
        <div className="text-center py-12">
          <p className="text-gray-500">Member not found</p>
        </div>
      </LayoutWrapper>
    );
  }

  const age = calculateAge(member.dob);
  const spouseAge = calculateAge(member.spouse_dob);

  return (
    <LayoutWrapper
      title="Member Details"
      subtitle={`${member.membership_no} - ${member.applicant_name}`}
      actions={
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate('/members')} className="border-gray-300 hover:bg-gray-100 text-sm" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Back</span>
            <span className="sm:hidden">Back</span>
          </Button>
          <Button variant="outline" onClick={handlePrint} className="border-blue-600 text-blue-600 hover:bg-blue-50 text-sm" data-testid="print-btn">
            <Printer className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Print</span>
            <span className="sm:hidden">Print</span>
          </Button>
          {canEdit && (
            <Button onClick={() => navigate(`/members/edit/${id}`)} className="bg-blue-600 hover:bg-blue-700 text-white text-sm" data-testid="edit-btn">
              <Edit className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Edit</span>
              <span className="sm:hidden">Edit</span>
            </Button>
          )}
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Profile Card */}
        <Card className="border-gray-200">
          <CardContent className="pt-6">
            <div className="text-center">
              {member.profile_picture ? (
                
              <img
                  src={getImageUrl(member.profile_picture)}
                  alt="Profile"
                  className="w-24 h-24 sm:w-32 sm:h-32 rounded-full object-cover mx-auto shadow-lg"
              />


              ) : (
                <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gray-200 flex items-center justify-center mx-auto shadow-lg">
                  <UserCircle className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400" />
                </div>
              )}
              <h2 className="text-lg sm:text-xl font-bold mt-4 text-gray-900">{member.applicant_name}</h2>
              <p className="text-gray-500 text-sm">{member.membership_no}</p>
              <div className="mt-2 flex flex-wrap justify-center gap-2">
                <Badge variant={member.membership_type === 'Permanent' ? 'default' : 'secondary'} className="bg-blue-100 text-blue-800 text-xs">
                  {member.membership_type}
                </Badge>
                <Badge
                  variant={member.status === 'Active' ? 'success' : 'destructive'}
                  className={`text-xs ${member.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                >
                  {member.status}
                </Badge>
              </div>

              <div className="mt-6 space-y-3 text-left">
                {member.mobile && (
                  <div className="flex items-center gap-3 text-gray-600">
                    <Phone className="w-4 h-4 flex-shrink-0" />
                    <span className="break-all text-sm">{member.mobile}</span>
                  </div>
                )}
                {member.email && (
                  <div className="flex items-center gap-3 text-gray-600">
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate text-xs sm:text-sm">{member.email}</span>
                  </div>
                )}
                {member.address && (
                  <div className="flex items-start gap-3 text-gray-600">
                    <MapPin className="w-4 h-4 flex-shrink-0 mt-1" />
                    <span className="text-xs sm:text-sm">{member.address}</span>
                  </div>
                )}
                <div className="flex items-center gap-3 text-gray-600">
                  <Calendar className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm">Joined: {member.date}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Details */}
        <div className="lg:col-span-2 space-y-4 sm:space-y-6">
          {/* Personal Information */}
          <Card className="border-gray-200">
            <CardHeader className="bg-gray-50">
              <CardTitle className="text-gray-900 text-base sm:text-lg">Personal Information</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 font-medium">Father's Name</p>
                  <p className="text-gray-900 break-words text-sm">{member.father_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 font-medium">Date of Birth</p>
                  <p className="text-gray-900 text-sm">
                    {member.dob || '-'}
                    {age && <span className="text-blue-600 ml-2">({age} years)</span>}
                  </p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 font-medium">Sex</p>
                  <p className="text-gray-900 text-sm">{member.sex || '-'}</p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 font-medium">Marital Status</p>
                  <p className="text-gray-900 text-sm">{member.marital_status || '-'}</p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 font-medium">Nationality</p>
                  <p className="text-gray-900 text-sm">{member.nationality || '-'}</p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 font-medium">Aadhaar</p>
                  <p className="text-gray-900 break-all text-sm">{member.aadhaar || '-'}</p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 font-medium">PAN</p>
                  <p className="text-gray-900 text-sm">{member.pan || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Professional Details */}
          <Card className="border-gray-200">
            <CardHeader className="bg-gray-50">
              <CardTitle className="text-gray-900 text-base sm:text-lg">Professional Details</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 font-medium">Qualification</p>
                  <p className="text-gray-900 break-words text-sm">{member.qualification || '-'}</p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 font-medium">Organization</p>
                  <p className="text-gray-900 break-words text-sm">{member.organization || '-'}</p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 font-medium">Designation</p>
                  <p className="text-gray-900 break-words text-sm">{member.designation || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Subscription Period - Only for Temporary */}
          {member.membership_type === 'Temporary' && (
            <Card className="border-gray-200">
              <CardHeader className="bg-gray-50">
                <CardTitle className="text-gray-900 text-base sm:text-lg">Subscription Period</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500 font-medium">From Date</p>
                    <p className="text-gray-900 text-sm">{member.subscription_from || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500 font-medium">To Date</p>
                    <p className="text-gray-900 text-sm">{member.subscription_to || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Spouse Details */}
          {member.marital_status === 'Married' && member.spouse_name && (
            <Card className="border-gray-200">
              <CardHeader className="bg-gray-50">
                <CardTitle className="text-gray-900 text-base sm:text-lg">Spouse Details</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500 font-medium">Spouse Name</p>
                    <p className="text-gray-900 break-words text-sm">{member.spouse_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500 font-medium">Spouse DOB</p>
                    <p className="text-gray-900 text-sm">
                      {member.spouse_dob || '-'}
                      {spouseAge && <span className="text-blue-600 ml-2">({spouseAge} years)</span>}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500 font-medium">Qualification</p>
                    <p className="text-gray-900 break-words text-sm">{member.spouse_qualification || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500 font-medium">Occupation</p>
                    <p className="text-gray-900 break-words text-sm">{member.spouse_occupation || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Financial Details */}
          <Card className="border-gray-200">
            <CardHeader className="bg-gray-50">
              <CardTitle className="text-gray-900 text-base sm:text-lg">Financial Details</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 font-medium">Membership Fee</p>
                  <p className="text-gray-900 font-semibold text-sm">{(member.membership_fee || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 font-medium">Opening Balance</p>
                  <p className="text-gray-900 font-semibold text-sm">{(member.opening_balance || 0).toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Games of Interest */}
          {member.games_of_interest?.length > 0 && (
            <Card className="border-gray-200">
              <CardHeader className="bg-gray-50">
                <CardTitle className="text-gray-900 text-base sm:text-lg">Games of Interest</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="flex flex-wrap gap-2">
                  {member.games_of_interest.map((game) => (
                    <Badge key={game} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">{game}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Dependants */}
          {member.dependants?.length > 0 && (
            <Card className="border-gray-200">
              <CardHeader className="bg-gray-50">
                <CardTitle className="text-gray-900 text-base sm:text-lg">Dependants</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-3">
                  {member.dependants.map((dep, index) => {
                    const depAge = calculateAge(dep.dob);
                    return (
                      <div key={index} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 gap-2">
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{dep.name}</p>
                          <p className="text-xs sm:text-sm text-gray-500">
                            {dep.relation} • {dep.sex} • {dep.dob || 'DOB not provided'}
                            {depAge && <span className="text-blue-600 ml-2">({depAge} years)</span>}
                          </p>
                        </div>
                        {dep.occupation && (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 w-fit text-xs">{dep.occupation}</Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </LayoutWrapper>
  );
};

export default MemberView;
